import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Megaphone, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supplierLabels } from "@/data/supplierCategories";

export default function AdminSponsors() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/sponsors").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (sp) => { await api.patch(`/admin/sponsors/${sp.id}`, { active: !sp.active }); load(); };
  const remove = async (sp) => { await api.delete(`/admin/sponsors/${sp.id}`); toast.success("Sponsor removed"); load(); };

  return (
    <div className="space-y-3" data-testid="admin-sponsors">
      <p className="text-sm text-muted-foreground">Advertising partners shown first in the AI assistant when a manager's question matches their category or keywords. Pause or remove any listing here.</p>
      {items.length ? items.map((sp) => (
        <div key={sp.id} className="bg-card border border-border rounded-2xl p-5 shadow-sm flex items-start gap-4 flex-wrap" data-testid={`admin-sponsor-${sp.id}`}>
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0"><Megaphone className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display font-semibold">{sp.company}</p>
              <Badge variant="secondary">{supplierLabels(sp)}</Badge>
              <Badge variant="outline" className="capitalize" data-testid={`admin-sponsor-tier-${sp.id}`}>{sp.tier || "partner"}</Badge>
              <Badge variant={sp.active ? "default" : "outline"} className={sp.active ? "bg-accent border-0" : ""} data-testid={`admin-sponsor-status-${sp.id}`}>{sp.status}</Badge>
              <span className="text-xs text-muted-foreground ml-auto">{sp.impressions || 0} impressions · {sp.clicks || 0} clicks</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{sp.description}</p>
            <p className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
              <a href={sp.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">{sp.url} <ExternalLink className="w-3 h-3" /></a>
              <span>{sp.contact_email}</span>{sp.keywords?.length > 0 && <span>Keywords: {sp.keywords.join(", ")}</span>}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <label className="flex items-center gap-2 text-xs"><Switch checked={!!sp.active} onCheckedChange={() => toggle(sp)} data-testid={`admin-sponsor-toggle-${sp.id}`} /> Live</label>
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(sp)} data-testid={`admin-sponsor-delete-${sp.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      )) : <p className="text-center text-muted-foreground py-12" data-testid="admin-sponsors-empty">No advertising partners yet. Share <span className="font-mono text-xs">/advertise</span> with training providers and suppliers.</p>}
    </div>
  );
}
